import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { NotificationLog } from '../../../flowpoint-apps/station-app/interfaces/notification-log';
import { AppDatePipe } from '../../pipes/app-date.pipe';
import { DateTimeSettingsState } from '../../state-stores/date-time-settings.state';

@Component({
  selector: 'app-notification-panel',
  standalone: true,
  imports: [CommonModule, TranslocoModule, AppDatePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './notification-panel.component.html',
})
export class NotificationPanelComponent {
  @Input() logs: NotificationLog[] = [];
  @Input() loading = false;
  @Input() error = false;
  @Input() readIds: Set<number> = new Set();

  @Output() notificationClicked = new EventEmitter<{ source?: string; id?: number }>();

  constructor(private readonly dateTimeSettings: DateTimeSettingsState) {}

  get dateFormat() { return this.dateTimeSettings.dateFormat; }
  get dateTimezone() { return this.dateTimeSettings.timezone; }

  isRead(id: number | undefined): boolean {
    return id != null && this.readIds.has(id);
  }

  onItemClick(log: NotificationLog): void {
    this.notificationClicked.emit({ source: log.source, id: log.id });
  }

  getPriorityRowClass(log: NotificationLog): Record<string, boolean> {
    const unread = !this.isRead(log.id);
    const p = log.priority;
    return {
      'border-l-4': true,
      'hover:brightness-95': true,
      'bg-red-50 border-red-400':       unread && p === 'Critical',
      'bg-orange-50 border-orange-400': unread && p === 'High',
      'bg-yellow-50 border-yellow-400': unread && p === 'Normal',
      'bg-blue-50 border-blue-300':     unread && (p === 'Low' || !p),
      'bg-white border-transparent hover:bg-gray-50': !unread,
    };
  }

  getPriorityBadgeClass(priority: string): Record<string, boolean> {
    return {
      'bg-red-100 text-red-800 ring-1 ring-red-300':       priority === 'Critical',
      'bg-orange-100 text-orange-800 ring-1 ring-orange-300': priority === 'High',
      'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-300': priority === 'Normal',
      'bg-blue-100 text-blue-800 ring-1 ring-blue-300':    priority === 'Low',
    };
  }

  getPriorityDotClass(priority: string): Record<string, boolean> {
    return {
      'bg-red-500':    priority === 'Critical',
      'bg-orange-500': priority === 'High',
      'bg-yellow-500': priority === 'Normal',
      'bg-blue-500':   priority === 'Low',
    };
  }
}
